import { Big } from "big.js";

export const VendorCommissionSource = {
  CONSIGNMENT: "consignment",
  VENDOR: "vendor",
  SALE: "sale",
  DEPLOYMENT: "deployment",
} as const;

export type VendorCommissionSourceType =
  (typeof VendorCommissionSource)[keyof typeof VendorCommissionSource];

export const VendorCommissionSourceLabel: Record<
  VendorCommissionSourceType,
  string
> = {
  [VendorCommissionSource.CONSIGNMENT]:
    "Consignment Vendor Commission Override",
  [VendorCommissionSource.VENDOR]: "Vendor's Default Vendor Commission",
  [VendorCommissionSource.SALE]: "Sale Default Vendor Commission",
  [VendorCommissionSource.DEPLOYMENT]: "Agency's Default Vendor Commission",
};

interface ResolveEffectiveVendorCommissionArgs {
  consignmentCommission?: Commission | null;
  deploymentCommission?: VendorCommissionBand | null;
  saleCommission?: Commission | null;
  vendorCommission?: VendorCommissionBand | null;
}

export interface EffectiveVendorCommission {
  commission: Commission | null;
  source: VendorCommissionSourceType | null;
}

export function resolveEffectiveVendorCommission({
  consignmentCommission = null,
  deploymentCommission = null,
  saleCommission = null,
  vendorCommission = null,
}: ResolveEffectiveVendorCommissionArgs): EffectiveVendorCommission | null {
  if (consignmentCommission) {
    return {
      commission: consignmentCommission,
      source: VendorCommissionSource.CONSIGNMENT,
    };
  }

  if (vendorCommission) {
    return {
      commission: vendorCommission,
      source: VendorCommissionSource.VENDOR,
    };
  }

  if (saleCommission) {
    return {
      commission: saleCommission,
      source: VendorCommissionSource.SALE,
    };
  }

  if (deploymentCommission) {
    return {
      commission: deploymentCommission,
      source: VendorCommissionSource.DEPLOYMENT,
    };
  }

  return null;
}

export function getVendorCommissionSourceLabel(
  source: VendorCommissionSourceType | null | undefined,
): string {
  return source ? VendorCommissionSourceLabel[source] : "";
}

export function commissionsAreEqual(
  a?: Commission | null,
  b?: Commission | null,
): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return (
    a.commissionType === b.commissionType &&
    Big(a.commissionValue).eq(b.commissionValue)
  );
}
